import type { FetchInfiniteQueryOptions, FetchQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query'
import {
  difficultWordsQuery, progressSummaryQuery, recentDecksQuery, sessionStatusQuery, smartQueueSessionQuery,
  vocabularyMapQuery,
} from '../hooks/useProgress'
import { decksQuery } from '../hooks/useDecks'
import { foldersQuery } from '../hooks/useFolders'
import { textsListQuery } from '../hooks/useTexts'
import { catalogCoursesQuery, catalogDecksQuery } from '../hooks/useCatalog'
import { publicDecksInfiniteQuery } from '../hooks/usePublic'
import { stopState } from '../stores/stopMemory'
import { SWIPE_CHAIN, type ChainStop } from './swipeChain'
import { isProOrTrial } from './plan'
import type { User } from '../types'

/**
 * Neighbour data preloading — the data a chain stop's neighbours show on
 * their first view is requested in the background once the current stop has
 * settled, so arriving there (by swipe or by tap, on any width) finds it in the
 * cache and shows content instead of a skeleton.
 *
 * Only data is loaded: nothing is rendered or mounted in advance, so nothing a
 * real visit does (page view, celebrations, prompts, timers, P4 writes) runs.
 *
 * Every tunable of it is here.
 */
export const NEIGHBOUR_PREFETCH = {
  enabled: true,
  /** How long to wait for the browser to be idle before giving up and going anyway (ms). */
  idleTimeoutMs: 2000,
  /** Without `requestIdleCallback` (Safari): wait this long after the page went quiet (ms). */
  idleFallbackMs: 300,
  /** Connection types too slow to spend on data the learner may never open. */
  slowConnections: ['slow-2g', '2g'] as readonly string[],
} as const

/** One background request: its key, and how to make it (a query, or an infinite query's first pages). */
export type PrefetchItem =
  | { kind: 'fetch'; queryKey: QueryKey; run: (qc: QueryClient) => Promise<void> }
  /** A query that depends on another's answer (Home's session status needs the session). */
  | { kind: 'then'; after: (qc: QueryClient) => PrefetchItem[] }

function q<T, K extends QueryKey>(options: FetchQueryOptions<T, Error, T, K>): PrefetchItem {
  return { kind: 'fetch', queryKey: options.queryKey, run: (qc) => qc.prefetchQuery(options) }
}
function infinite<T, K extends QueryKey>(options: FetchInfiniteQueryOptions<T, Error, T, K, number>, pages: number): PrefetchItem {
  // `pages` > 1 loads that many pages in one go, following `getNextPageParam`.
  return { kind: 'fetch', queryKey: options.queryKey, run: (qc) => qc.prefetchInfiniteQuery({ ...options, pages } as typeof options) }
}

/** Mine's page size — must match `PAGE_SIZE` in LibraryPage (asserted by the tests). */
export const MINE_PAGE_SIZE = 40
/** Catalog page sizes and Explore's — as CatalogPage requests them. */
export const CATALOG_DECKS_PAGE_SIZE = 30
export const CATALOG_COURSES_PAGE_SIZE = 24
export const EXPLORE_PAGE_SIZE = 20

/** The chain stops next to `stop`: the one to the right first, then the one to the left. */
export function chainNeighbours(stop: ChainStop): ChainStop[] {
  const i = SWIPE_CHAIN.indexOf(stop)
  return [SWIPE_CHAIN[i + 1], SWIPE_CHAIN[i - 1]].filter((s): s is ChainStop => s !== undefined)
}

/**
 * What `stop` requests for its first view, from its remembered state (P4) or
 * its defaults — the same keys, fetchers and staleness its own hooks use. Data
 * the learner is not entitled to is left out: Catalog › Courses is Pro-only on
 * the server (`require_pro_plan` records every refusal as a paywall encounter),
 * so it is requested only with Pro or an active trial.
 */
export function stopDataPlan(stop: ChainStop, user: User | null): PrefetchItem[] {
  switch (stop) {
    case 'home':
      return [
        q(progressSummaryQuery(true)),
        q(smartQueueSessionQuery(true)),
        // The Continue label needs the paused session's status, once it is known.
        {
          kind: 'then',
          after: (qc) => {
            const session = qc.getQueryData(smartQueueSessionQuery().queryKey)
            return session?.session_id ? [q(sessionStatusQuery(session.session_id, true))] : []
          },
        },
        q(vocabularyMapQuery(true)),
        q(recentDecksQuery(true)),
      ]
    case 'mine': {
      // Both segments' lists, whichever is shown: the «Колоды {n} | Тексты {n}»
      // switch counts both on arrival.
      const m = stopState('mine')
      return [
        q(decksQuery(m.page, MINE_PAGE_SIZE, m.folderId, m.search || undefined, true)),
        q(foldersQuery(true)),
        q(textsListQuery(true)),
      ]
    }
    case 'catalog-decks': {
      const d = stopState('catalogDecks')
      return [q(catalogDecksQuery({ level: d.level, page: d.page, pageSize: CATALOG_DECKS_PAGE_SIZE }, true))]
    }
    case 'catalog-courses': {
      if (!isProOrTrial(user)) return []
      const c = stopState('catalogCourses')
      return [q(catalogCoursesQuery({ language: c.language, level: c.level, page: c.page, pageSize: CATALOG_COURSES_PAGE_SIZE }, true))]
    }
    case 'catalog-explore': {
      // Explore opens on the typed text (completing an interrupted apply, P4),
      // and reloads the remembered "load more" extent only when that text had
      // already been applied — otherwise it is a new list, from its first page.
      const e = stopState('catalogExplore')
      const pages = e.searchInput === e.search ? Math.max(1, e.pageCount) : 1
      return [infinite(publicDecksInfiniteQuery(EXPLORE_PAGE_SIZE, e.searchInput || undefined, true, true), pages)]
    }
    case 'progress':
      return [q(progressSummaryQuery(true)), q(vocabularyMapQuery(true)), q(difficultWordsQuery(true))]
  }
}

/** Everything the neighbours of `stop` show on their first view, nearest-right first. */
export function neighbourPlan(stop: ChainStop, user: User | null): PrefetchItem[] {
  return chainNeighbours(stop).flatMap((s) => stopDataPlan(s, user))
}

/**
 * Whether a background request is worth making for `queryKey`: only if its
 * data is not in the cache at all, or has been invalidated (a change the app
 * itself made). Cached data — fresh or stale — already renders on arrival
 * without a skeleton, and the stop's own mount refreshes it as it does today;
 * fetching it here too would only double the request. Never while it is
 * already being fetched.
 */
export function needsFetch(qc: QueryClient, queryKey: QueryKey): boolean {
  const query = qc.getQueryCache().find({ queryKey, exact: true })
  if (!query) return true
  if (query.state.fetchStatus === 'fetching') return false
  return query.state.data === undefined || query.state.isInvalidated
}

/** The browser asks to save data, or the connection is too slow to spend on it. */
export function connectionForbidsPrefetch(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
  if (!c) return false
  return c.saveData === true || (c.effectiveType !== undefined && NEIGHBOUR_PREFETCH.slowConnections.includes(c.effectiveType))
}
